/**
 * Registry deciding which mounted route currently speaks for the native ribbon.
 *
 * Arbitration is by monotonic epoch, never by teardown timing: a new claim makes
 * every older token permanently stale, so the late cleanup of a route that has
 * already been replaced can no longer overwrite what its successor published.
 */

export interface HostRibbonOwnerToken {
  readonly epoch: number;
  readonly label: string;
}

let lastClaimedEpoch = 0;
let currentOwner: HostRibbonOwnerToken | null = null;

/** Registers a new owner and supersedes whichever route owned the ribbon before. */
export const claimHostRibbonOwnership = (label: string): HostRibbonOwnerToken => {
  lastClaimedEpoch += 1;
  currentOwner = { epoch: lastClaimedEpoch, label };

  return currentOwner;
};

/** Reports whether the token still represents the ribbon's current owner. */
export const isHostRibbonOwner = (token: HostRibbonOwnerToken | null | undefined): boolean => {
  if (!token || !currentOwner) {
    return false;
  }

  return currentOwner.epoch === token.epoch;
};

/** Clears ownership for the current owner only; a stale token releases nothing. */
export const releaseHostRibbonOwnership = (
  token: HostRibbonOwnerToken | null | undefined
): boolean => {
  if (!isHostRibbonOwner(token)) {
    return false;
  }

  currentOwner = null;

  return true;
};
